import { Controller } from "@hotwired/stimulus"

// Controller for managing multi-organization donations on Giving Days
// Handles organization search dropdown and adding new organization donation cards
export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "searchContainer",
    "addButton"
  ]

  static values = {
    cartId: String,
    givingDayId: String,
    primaryOrgId: String,
    prominent: Boolean
  }

  connect() {
    console.log("Organization Selector Controller connected", {
      cartId: this.cartIdValue,
      givingDayId: this.givingDayIdValue,
      prominent: this.prominentValue
    })
    this.searchTimeout = null
    this.searchOpen = false

    // Close dropdown when clicking outside (only if not in prominent mode)
    if (!this.prominentValue) {
      document.addEventListener('click', this.handleClickOutside.bind(this))
    }

    // If in prominent mode, show search immediately
    if (this.prominentValue) {
      this.showSearch()
    }
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    document.removeEventListener('click', this.handleClickOutside.bind(this))
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSearch()
    }
  }

  toggleSearch(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.searchOpen) {
      this.hideSearch()
    } else {
      this.showSearch()
    }
  }

  showSearch() {
    if (this.hasSearchContainerTarget) {
      this.searchContainerTarget.classList.remove('d-none')
      this.searchOpen = true

      // Focus the search input
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus()
        // Show initial results
        this.searchOrganizations('')
      }
    }
  }

  hideSearch() {
    if (this.hasSearchContainerTarget) {
      this.searchContainerTarget.classList.add('d-none')
      this.searchOpen = false

      // Clear search input
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.value = ''
      }

      // Clear results
      if (this.hasSearchResultsTarget) {
        this.searchResultsTarget.innerHTML = ''
      }
    }
  }

  search(event) {
    clearTimeout(this.searchTimeout)
    const query = event.target.value.trim()

    // Debounce API search
    this.searchTimeout = setTimeout(() => {
      this.searchOrganizations(query)
    }, 200)
  }

  showInitialResults() {
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ''
    this.searchOrganizations(query)
  }

  async searchOrganizations(query) {
    try {
      let url = this.apiUrl('/api/v1/checkout_donations/organizations?per_page=10')
      if (query.length > 0) {
        url = this.apiUrl(`/api/v1/checkout_donations/organizations?q=${encodeURIComponent(query)}&per_page=10`)
      }

      const response = await fetch(url)
      const data = await response.json()

      this.displaySearchResults(data.organizations || [])
    } catch (error) {
      console.error("Error searching organizations:", error)
    }
  }

  displaySearchResults(organizations) {
    if (!this.hasSearchResultsTarget) return

    // Get list of already added organization IDs
    const existingOrgIds = this.getExistingOrgIds()

    // Filter out already selected organizations and the primary org
    const available = organizations.filter(org => {
      const orgId = org.id.toString()
      if (existingOrgIds.includes(orgId)) return false
      if (this.primaryOrgIdValue && orgId === this.primaryOrgIdValue.toString()) return false
      return true
    })

    if (available.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <div class="no-results">
          No organizations found
        </div>
      `
      return
    }

    const resultsHTML = available.map(org => `
      <div class="search-result-item"
           data-action="click->organization-selector#selectOrganization"
           data-org-id="${org.id}"
           data-org-name="${this.escapeHtml(org.name)}"
           data-org-logo="${org.logo_url || ''}"
           data-org-tagline="${this.escapeHtml(org.tagline || '')}">
        <span class="result-name">${this.escapeHtml(org.name)}</span>
      </div>
    `).join('')

    this.searchResultsTarget.innerHTML = resultsHTML
  }

  getExistingOrgIds() {
    // Get all organization IDs from existing donation cards
    const cards = document.querySelectorAll('.gv-org-donation-card')
    const ids = []
    cards.forEach(card => {
      const controller = this.application.getControllerForElementAndIdentifier(card, 'org-donation')
      if (controller && controller.orgIdValue) {
        ids.push(controller.orgIdValue.toString())
      }
    })
    return ids
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }

  async selectOrganization(event) {
    event.preventDefault()
    event.stopPropagation()

    const item = event.currentTarget
    const orgId = item.dataset.orgId
    const orgName = item.dataset.orgName
    const orgLogo = item.dataset.orgLogo
    const orgTagline = item.dataset.orgTagline
    const defaultAmount = 25.00

    this.hideSearch()

    // If in prominent mode (selecting primary org), reload page with organization_id
    if (this.prominentValue) {
      const url = new URL(window.location.href)
      url.searchParams.set('organization_id', orgId)
      window.location.href = url.toString()
      return
    }

    // Add via API (for additional orgs)
    try {
      const response = await fetch(this.apiUrl('/api/v1/checkout_donations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization_id: orgId,
          amount: defaultAmount
        })
      })

      const data = await response.json()

      if (data.donation) {
        // Create and add the donation card
        this.addDonationCard({
          id: data.donation.id,
          organizationId: orgId,
          name: orgName,
          logo: orgLogo,
          tagline: orgTagline,
          amount: parseFloat(data.donation.amount) || defaultAmount
        })

        // Update Purchase Summary
        this.dispatch("donationAdded", {
          detail: {
            donation: data.donation,
            cartTotal: data.cart_total,
            organizationName: orgName
          }
        })
      }
    } catch (error) {
      console.error("Error adding organization:", error)
    }
  }

  addDonationCard(org) {
    // Find the additional donations container
    const container = document.querySelector('[data-multi-org-donation-target="additionalDonations"]')
    if (!container) {
      console.error("Could not find additional donations container")
      return
    }

    // Create a new donation card HTML
    const cardHTML = this.createDonationCardHTML(org)

    // Insert before the organization selector
    container.insertAdjacentHTML('beforeend', cardHTML)
  }

  createDonationCardHTML(org) {
    const amounts = [5, 10, 25, 50, 100, 250]
    const amountOptions = amounts.map(amount => `
      <div class="amount-option">
        <label>
          <input type="radio" name="org_${org.organizationId}_amount" value="${amount}"
                 data-action="click->org-donation#selectPreset"
                 data-org-donation-target="preset"
                 ${amount === 25 ? 'checked' : ''}>
          <div class="amount-box">$${amount}.00</div>
        </label>
      </div>
    `).join('')

    return `
      <div class="gv-org-donation-card mb-4"
           data-controller="org-donation"
           data-org-donation-org-id-value="${org.organizationId}"
           data-org-donation-item-id-value="${org.id}"
           data-org-donation-cart-id-value="${this.cartIdValue}"
           data-org-donation-is-primary-value="false">

        <!-- Organization Header -->
        <div class="org-header d-flex align-items-center gap-3 mb-4">
          <div class="org-logo">
            ${org.logo
              ? `<img src="${org.logo}" alt="${this.escapeHtml(org.name)}" class="rounded">`
              : `<div class="org-logo-placeholder"><i class="fas fa-building"></i></div>`
            }
          </div>
          <div class="org-info">
            <h5 class="org-name mb-1">${this.escapeHtml(org.name)}</h5>
            <p class="org-description text-muted mb-0 small">${this.escapeHtml(org.tagline) || 'Lorem ipsum dolor amet sit'}</p>
          </div>
        </div>

        <!-- Donation Amount Selector -->
        <div class="gv-amount-selector mb-3">
          ${amountOptions}
        </div>

        <!-- Custom Amount Field -->
        <div class="gv-custom-amount mb-4">
          <input type="text" class="form-control" placeholder="Custom Amount"
                 data-action="input->org-donation#updateCustomAmount focus->org-donation#clearPresets"
                 data-org-donation-target="customAmount">
          <div class="currency-symbol">$</div>
        </div>

        <!-- Recurring Gift Toggle -->
        <div class="gv-checkbox mb-3">
          <div class="form-check">
            <input type="checkbox" class="form-check-input" id="recurring_${org.organizationId}"
                   data-action="change->org-donation#toggleRecurring"
                   data-org-donation-target="recurringCheckbox">
            <label class="form-check-label" for="recurring_${org.organizationId}">
              Make this a recurring gift
            </label>
          </div>
        </div>

        <!-- Recurring Options (hidden by default) -->
        <div class="recurring-options mb-3 d-none" data-org-donation-target="recurringOptions">
          <div class="row g-3">
            <div class="col-md-6">
              <select class="form-select" data-action="change->org-donation#saveRecurring" data-org-donation-target="frequency">
                <option value="monthly" selected>Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
            <div class="col-md-6">
              <select class="form-select" data-action="change->org-donation#toggleEndDate" data-org-donation-target="endType">
                <option value="never" selected>Never Ends</option>
                <option value="date">Until Date</option>
              </select>
            </div>
          </div>
          <div class="row g-3 mt-2 d-none" data-org-donation-target="endDateRow">
            <div class="col-12">
              <input type="date" class="form-control" data-action="change->org-donation#saveRecurring" data-org-donation-target="endDate">
            </div>
          </div>
        </div>

        <!-- New Donor Checkbox -->
        <div class="gv-checkbox mb-3">
          <div class="form-check">
            <input type="checkbox" class="form-check-input" id="new_donor_${org.organizationId}"
                   data-org-donation-target="newDonorCheckbox"
                   data-action="change->org-donation#saveNewDonor">
            <label class="form-check-label" for="new_donor_${org.organizationId}">
              I am a new donor to this organization
              <i class="fas fa-info-circle text-muted ms-1" data-bs-toggle="tooltip"
                 title="Check this if this is your first donation to this organization"></i>
            </label>
          </div>
        </div>

        <!-- Tribute/Comment Toggle -->
        <div class="gv-toggle-section mb-3">
          <div class="gv-toggle" data-action="click->org-donation#toggleTribute"
               data-org-donation-target="tributeToggle">
            <div class="form-check mb-0">
              <input type="checkbox" class="form-check-input" id="tribute_${org.organizationId}"
                     data-org-donation-target="tributeCheckbox">
              <label class="form-check-label" for="tribute_${org.organizationId}">
                Make a comment/tribute for public display
              </label>
            </div>
          </div>
          <div class="tribute-section mt-3 d-none" data-org-donation-target="tributeSection">
            <textarea class="form-control" placeholder="Extended Field Title" rows="4"
                      data-org-donation-target="tributeText"></textarea>
          </div>
        </div>

        <!-- Remove Button -->
        <div class="text-end">
          <button type="button" class="btn-gv-remove btn-link" data-action="click->org-donation#removeDonation">
            <i class="fas fa-trash-alt me-2"></i>
            Remove Donation
          </button>
        </div>
      </div>
    `
  }

  // Build API URL with cart_id parameter
  apiUrl(path) {
    const url = new URL(path, window.location.origin)
    if (this.cartIdValue) {
      url.searchParams.set('cart_id', this.cartIdValue)
    }
    if (this.givingDayIdValue) {
      url.searchParams.set('giving_day_id', this.givingDayIdValue)
    }
    return url.toString()
  }
}
